import type { ScriptConverter, ScriptOutputOption } from './base.js';
import { BashScriptConverter } from './lang/bash.js';
import { PowerShellScriptConverter } from './lang/powershell.js';

/**
 * Registry by file extension (lowercase, including leading dot).
 * Add new extensions here to support additional script languages.
 */
const REGISTERED_CONVERTERS: ScriptConverter[] = [
  new BashScriptConverter(),
  new PowerShellScriptConverter(),
];

const CONVERTERS_BY_EXTENSION: Record<string, ScriptConverter> = {};
for (const converter of REGISTERED_CONVERTERS) {
  for (const extension of converter.extensions) {
    CONVERTERS_BY_EXTENSION[extension] = converter;
  }
}

/**
 * Fallback for unknown extensions.
 * Preserves legacy behavior.
 */
const defaultConverter = new BashScriptConverter();

/**
 * Get lowercase file extension including leading dot.
 */
export function getScriptExtension(scriptName: string): string {
  const lastDot = scriptName.lastIndexOf('.');
  if (lastDot <= 0 || lastDot === scriptName.length - 1) return '';
  return scriptName.slice(lastDot).toLowerCase();
}

/**
 * Resolve converter for a script filename.
 */
export function getScriptConverter(scriptName: string): ScriptConverter {
  const ext = getScriptExtension(scriptName);
  return CONVERTERS_BY_EXTENSION[ext] || defaultConverter;
}

/**
 * Compose blocks for a specific script filename.
 */
export function composeScriptBlocks(scriptName: string, blocks: string[]): string {
  return getScriptConverter(scriptName).composeBlocks(blocks);
}

/**
 * Get output mode options for a script filename.
 */
export function getScriptOutputOptions(scriptName: string): ScriptOutputOption[] {
  return getScriptConverter(scriptName).getOutputOptions();
}

/**
 * Check whether a specific output mode is supported for a script filename.
 */
export function scriptSupportsOutputMode(scriptName: string, mode: string): boolean {
  return getScriptConverter(scriptName).supportsOutputMode(mode);
}
